import { Button, Table, Modal, message } from 'antd';
import { useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import qs from 'qs';

import IconDelete from 'assets/images/IconDelete.svg';
import IconEdit from 'assets/images/IconEdit.svg';

//requests
import { getListClass, deleteClass, uploadClasses } from 'requests/class';

//Components
import TableAction from 'components/TableAction';

const ClassPage = () => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [records, setRecords] = useState([]);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [currentQuery, setCurrentQuery] = useState({});

    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const authUser = useSelector((state) => state.auth.authUser);

    const { confirm } = Modal;

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
        },
        {
            title: t('class_list'),
            dataIndex: 'className',
            render: (text, record, index) => (
                <Link to={`/classes/${record.id}/list`} type="link">
                    {text}
                </Link>
            ),
        },
        {
            title: t('student_number'),
            dataIndex: 'studentsCount',
        },
        {
            title: t('teacher_charge'),
            dataIndex: 'teachers',
            render: (text, record, index) => (
                <div>
                    {record.teachers.map((e, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && <span>, </span>}
                            <Link to={`/teachers/${e.id}/edit`} className="nowrap">
                                {e.name}
                            </Link>
                        </React.Fragment>
                    ))}
                </div>
            ),
        },
        {
            title: t('action'),
            render: (text, record, index) => (
                <div className="nowrap">
                    <Link to={`/classes/${record.id}/edit`} type="link">
                        <img src={IconEdit} alt="" />
                    </Link>
                    <Button type="link" onClick={() => handleClickDeleteBtn(record.id)}>
                        <img src={IconDelete} alt="" />
                    </Button>
                </div>
            ),
        },
    ];

    useEffect(() => {
        getData();
    }, [location]);

    const getData = async () => {
        try {
            setLoading(true);

            const queryString = location.search.slice(1);
            const query = qs.parse(queryString);

            setCurrentQuery(query);
            const page = query.page || 1;
            const takeCount = query.pageSize || 10;
            const skipCount = (page - 1) * takeCount;
            const data = await getListClass({ search: query.search, take: takeCount, skip: skipCount });

            if (data.total > 0 && page > Math.ceil(data.total / data.take)) {
                const newQuery = {
                    ...query,
                    page: Math.ceil(data.total / data.take),
                };
                const queryString = qs.stringify(newQuery);

                return navigate({
                    pathname: location.pathname,
                    search: `?${queryString}`,
                });
            }

            setRecords(data.records);
            setPageSize(data.take);
            setTotal(data.total);
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        } finally {
            setLoading(false);
        }
    };

    const handleClickDeleteBtn = (id) => {
        // modal confirm
        confirm({
            title: t('confirm_delete_one'),
            icon: <ExclamationCircleOutlined />,
            cancelText: t('cancel'),
            onOk() {
                handleDelete(id);
            },
        });
    };

    // delete single record
    const handleDelete = async (id) => {
        try {
            setLoading(true);
            await deleteClass({ classIds: [id] });
            getData();
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        } finally {
            setLoading(false);
        }
    };

    let handleDeleteMany = async () => {
        try {
            setLoading(true);
            await deleteClass({ classIds: selectedRowKeys });

            setSelectedRowKeys([]);
            getData();
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        } finally {
            setLoading(false);
        }
    };

    let handleAdd = () => {
        navigate('/classes/create');
    };

    let handleUpload = (e) => {
        const file = e.target.files[0];
        const formData = new FormData();
        formData.append('list', file);

        setLoading(true);
        uploadClasses(formData)
            .then(() => {
                message.success(t('success'));
                navigate({
                    pathname: location.pathname,
                });
            })
            .catch((err) => {
                console.log(err);
                const errors = err.response?.data.response;
                if (typeof errors === 'object') {
                    for (const key in errors) {
                        message.error(`${key}: ${t(errors[key])}`);
                    }
                } else {
                    message.error(t(errors));
                }
            })
            .finally(() => {
                setLoading(false);
                e.target.value = '';
            });
    };

    const handleSearch = async (value) => {
        const query = {
            ...currentQuery,
            search: value,
            page: 1,
        };

        const queryString = qs.stringify(query);
        navigate({
            pathname: location.pathname,
            search: `?${queryString}`,
        });
    };

    const handleSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const handlePageChange = (page, pageSize) => {
        setSelectedRowKeys([]);

        const query = {
            ...currentQuery,
            page: page,
            pageSize: pageSize,
        };

        const queryString = qs.stringify(query);
        navigate({
            pathname: location.pathname,
            search: `?${queryString}`,
        });
    };

    let rowSelection = {
        selectedRowKeys,
        onChange: handleSelectChange,
    };

    const hasSelected = selectedRowKeys.length > 0;
    if (authUser?.role === 2) {
        columns.splice(-2);
        handleAdd = null;
        handleDeleteMany = null;
        handleUpload = null;
        rowSelection = null;
    }

    return (
        <>
            <div className="header-panel">
                <div className="title ml-24">{t('class_page')}</div>
                <TableAction
                    query={currentQuery}
                    searchPlaceholder={t('search_class')}
                    onSearch={handleSearch}
                    onAdd={handleAdd}
                    onDelete={handleDeleteMany}
                    disableDeleteBtn={!hasSelected}
                    onUpload={handleUpload}
                />
            </div>
            <Table
                rowKey="id"
                rowSelection={authUser.role === 1 ? rowSelection : null}
                columns={columns}
                dataSource={records}
                loading={loading}
                pagination={{
                    pageSize: pageSize,
                    total: total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    current: Number(currentQuery.page) || 1,
                    onChange: (page, pageSize) => handlePageChange(page, pageSize),
                }}
            />
        </>
    );
};

export default ClassPage;
